import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle,
  Brain,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const TimelineEntry = ({
  entry,
  idx,
  examEntries,
  selectedExams,
  expandedEntry,
  setExpandedEntry,
  onSelectExam,
}) => {
  const entryKey = `${entry.type}-${entry.id}`;
  const isExpanded = expandedEntry === entryKey;

  return (
    <div className="relative pl-14 pb-6" data-testid={`timeline-entry-${idx}`}>
      <div className={`absolute left-4 w-5 h-5 rounded-full border-2 border-white z-10 ${
        entry.type === 'exam' ? 'bg-[#4B6651]' : 'bg-[#C06144]'
      }`} />

      <Card className={`border-[#E5E4E0] bg-white ${
        entry.type === 'exam' && selectedExams.includes(entry.id) ? 'ring-2 ring-[#4B6651]' : ''
      }`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {entry.type === 'exam' ? (
                <span className="px-2 py-0.5 bg-[#4B6651]/10 text-[#4B6651] rounded text-xs font-medium">
                  Exam #{entry.index}
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-[#C06144]/10 text-[#C06144] rounded text-xs font-medium">
                  <Brain className="w-3 h-3 inline mr-1" />Analysis
                </span>
              )}
              <span className="text-xs text-[#5C5F5C]">
                {entry.date ? new Date(entry.date).toLocaleString() : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {entry.type === 'exam' && examEntries.length >= 2 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSelectExam(entry.id)}
                  className={`text-xs ${selectedExams.includes(entry.id) ? 'text-[#4B6651] bg-[#4B6651]/10' : 'text-[#5C5F5C]'}`}
                  data-testid={`select-exam-${entry.id}`}
                >
                  {selectedExams.includes(entry.id) ? 'Selected' : 'Select'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedEntry(isExpanded ? null : entryKey)}
              >
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {entry.type === 'exam' && (
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="text-[#5C5F5C]">Status: <span className="text-[#2A2C2A] font-medium">{entry.mental_status}</span></span>
              <span className="text-[#5C5F5C]">Gait: <span className="text-[#2A2C2A] font-medium">{entry.gait}</span></span>
              {entry.change_summary && (
                <div className="flex gap-2 ml-auto">
                  {entry.change_summary.new_abnormal > 0 && (
                    <span className="flex items-center gap-1 text-xs text-[#D94F4F]">
                      <TrendingDown className="w-3 h-3" /> {entry.change_summary.new_abnormal} new
                    </span>
                  )}
                  {entry.change_summary.resolved > 0 && (
                    <span className="flex items-center gap-1 text-xs text-[#4B6651]">
                      <TrendingUp className="w-3 h-3" /> {entry.change_summary.resolved} resolved
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          {entry.type === 'analysis' && (
            <div className="flex flex-wrap gap-2 text-sm">
              {entry.locations?.map((loc, i) => (
                <span key={i} className="px-2 py-0.5 bg-[#C06144]/10 text-[#C06144] rounded text-xs">{loc}</span>
              ))}
              <span className="text-xs text-[#5C5F5C]">{entry.abnormal_count} abnormal finding{entry.abnormal_count !== 1 ? 's' : ''}</span>
            </div>
          )}

          {isExpanded && entry.changes && (
            <div className="mt-3 pt-3 border-t border-[#E5E4E0] space-y-2">
              <p className="text-xs font-semibold text-[#5C5F5C] uppercase">Changes from Previous Exam</p>
              {entry.changes.new_abnormal.map((c, i) => (
                <div key={`na-${i}`} className="flex items-center gap-2 text-xs">
                  <AlertTriangle className="w-3 h-3 text-[#D94F4F]" />
                  <span>{c.label}: {c.from || 'normal'} → <span className="font-medium text-[#D94F4F]">{c.to}</span></span>
                </div>
              ))}
              {entry.changes.resolved.map((c, i) => (
                <div key={`r-${i}`} className="flex items-center gap-2 text-xs">
                  <CheckCircle className="w-3 h-3 text-[#4B6651]" />
                  <span>{c.label}: {c.from} → <span className="font-medium text-[#4B6651]">{c.to || 'normal'}</span></span>
                </div>
              ))}
              {entry.changes.worsened.map((c, i) => (
                <div key={`w-${i}`} className="flex items-center gap-2 text-xs">
                  <TrendingDown className="w-3 h-3 text-[#C06144]" />
                  <span>{c.label}: {c.from} → <span className="font-medium text-[#C06144]">{c.to}</span></span>
                </div>
              ))}
              {entry.changes.new_abnormal.length === 0 && entry.changes.resolved.length === 0 && entry.changes.worsened.length === 0 && (
                <div className="flex items-center gap-2 text-xs text-[#5C5F5C]">
                  <Minus className="w-3 h-3" />
                  <span>No significant changes</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TimelineEntry;
